import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { Observable, firstValueFrom, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { localStorageService } from "./components/shared/localstorage.service";
import { ApiService } from "./components/shared/api.service";
import { MainService } from "./components/shared/main.service";
import { PermissionsService } from "./components/shared/permissions/permissions.service";
import { embedToken } from "./components/shared/embed-host";

// Post-redirect URLs that mean "no particular destination": `/` redirects to
// `/app`, which redirects to `/app/dashboard`, so a plain visit to the site
// root reaches this guard as `/app/dashboard`. Capturing that as a returnUrl
// would override the permission-aware redirect LoginComponent runs at login.
const DEFAULT_LANDING_URLS = ['/', '/app', '/app/dashboard'];

export function captureReturnUrl(url: string | null | undefined): string | undefined {
    if (!url) {
        return undefined;
    }
    const path = url.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
    if (path.startsWith('/login') || DEFAULT_LANDING_URLS.includes(path)) {
        return undefined;
    }
    return url;
}

export const authGuard: CanActivateFn = async (route, state) => {
    const router = inject(Router);
    const _local = inject(localStorageService);
    // Embedded with a host token (?token=...&embed=prism, see shared/embed-host): sign in
    // with it in place instead of showing the login form.
    const hostToken = embedToken();
    if (hostToken && await firstValueFrom(completeEmbeddedSession(hostToken))) {
        return true;
    }
    if (_local.customGetItem('token') && _local.customGetItem('password_expired')) {
        router.navigate(['/force-password-change'], { queryParamsHandling: 'merge' });
        return false;
    }
    if (await isAuthenticated()) {
        return true;
    } else {
        _local.customClearAllItems();
        // Preserve the route the user was trying to reach so we can send them
        // back there after a successful login (session-expired flow). Default
        // landing routes are deliberately not captured — `/` redirects to
        // `/app/dashboard`, and pinning that here would override the
        // permission-aware redirect LoginComponent does after login.
        const returnUrl = captureReturnUrl(state.url);
        router.navigate(['/login'], {
            queryParamsHandling: 'merge',
            queryParams: returnUrl ? { returnUrl } : undefined
        });
        return false;
    }
};

/**
 * Stores the session the way LoginComponent does, from the host's access token. Done once
 * per token: a different token (another admin signed in to the host in this browser) must
 * reload the profile and menu, or the frame would show the previous admin's data.
 * Resolves to false when the token is rejected, so the guard falls back to the login form.
 */
function completeEmbeddedSession(hostToken: string): Observable<boolean> {
    const _local = inject(localStorageService);
    const _api = inject(ApiService);
    const _main = inject(MainService);
    const _permissions = inject(PermissionsService);
    if (_local.customGetItem('token') && _local.customGetItem('embed_session_token') === hostToken && _local.customGetItem('admin_profile')) {
        return of(true);
    }
    _local.customClearAllItems();
    // The interceptor reads the Authorization header from this key; the backend verifies the
    // host's token and answers with a regular panel session token (see auth/token_login).
    _local.customSetItem('token', hostToken);
    return _api.api({}, 'auth/token_login').pipe(
        map((response: any) => {
            if (!response || response.is_error || !response.admin_profile || !response.admin_profile.length) {
                _local.customClearAllItems();
                return false;
            }
            _local.customSetItem('token', response.access_token);
            _local.customSetItem('embed_session_token', hostToken);
            _local.customSetItem('admin_profile', response.admin_profile[0]);
            _local.customSetItem('show_gmail_connect_modal', false);
            _permissions.setFromLoginResponse(response);
            _main.fetchAdminList();
            if (response.menu && response.menu.length) {
                _main.menu$.next(response.menu);
            }
            return true;
        }),
        catchError(() => {
            _local.customClearAllItems();
            return of(false);
        })
    );
}

async function isAuthenticated() {
    const _local = new localStorageService();
    if (!_local.customGetItem('token') || _local.customGetItem('token') == null || _local.customGetItem('reset_password_token')) {
        return false;
    } else {
        return true;
    }
}